import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, PlusCircle } from "feather-icons-react/build/IconComponents";
import Select from "react-select";
import { DatePicker } from "antd";
import { useGetAllSupplierQuery, useGetSupplierByIdQuery, useUpdateSupplierMutation } from "../../core/redux/api/userApi";
import dayjs from 'dayjs';
import { useCreateSupplierTransactionMutation, useGetSupplierTransactionQuery } from "../../core/redux/api/OrderApi";
import Table from "../../core/pagination/datatable";
import toast from "react-hot-toast";
import { useFormik } from 'formik';
import * as Yup from 'yup';

const AddSupplierTransaction = () => {
  const { data: supplierResponse, refetch: refetchSupplierData } = useGetAllSupplierQuery();
  const [updateSupplier] = useUpdateSupplierMutation();
  const [createSupplierTransaction] = useCreateSupplierTransactionMutation();
  const { data: supplierTransactionData, refetch: refetchSupplierTransactionData } = useGetSupplierTransactionQuery();
  const [isLoading, setIsLoading] = useState(false);
  


  const suppliersList = supplierResponse?.map(item => ({
    value: item?._id,
    label: item?.supplierName
  }));

  const validationSchema = Yup.object().shape({
    selectedSupplier: Yup.object().required('Supplier is required'),
    date: Yup.date().required('Date is required'),
    amount: Yup.number()
      .required('Amount is required')
      .positive('Amount must be positive'),
    remarks: Yup.string(),
    transactionType: Yup.string().required('Transaction type is required')
  });

  const formik = useFormik({
    initialValues: {
      selectedSupplier: null,
      date: dayjs(),
      amount: '',
      remarks: '',
      transactionType: 'credit',
      supplierDetails: {
        supplierName: '',
        phone: '',
        currentBalance: '',
        address: ''
      }
    },
    validationSchema,
    onSubmit: async (values) => {
      if (!values.selectedSupplier || !values.amount) {
        toast.error('Please select a supplier and enter amount');
        return;
      }
    
      setIsLoading(true);
    
      try {
        const amount = parseFloat(values.amount);
        
        const transactionData = {
          supplierId: values.selectedSupplier.value,
          amount: amount,
          type: values.transactionType.toLowerCase(),
          remarks: values.remarks || '',
        };
    
        await createSupplierTransaction(transactionData).unwrap();
        
        await Promise.all([
        
          refetchSupplierTransactionData(),
          singleSupplierDataRefetch()
        ]);
    
        // Reset form
        formik.resetForm({
          values: {
            ...formik.initialValues,
            date: dayjs(),
            supplierDetails: formik.initialValues.supplierDetails
          }
        });
        
        toast.success("Transaction submitted successfully");
        refetchSupplierData();
      } catch (error) {
        console.error('Transaction failed:', error);
        toast.error(error.data?.message || 'Transaction failed');
      } finally {
        setIsLoading(false);
      }
    }
  });
  const { data: singleSupplier, refetch: singleSupplierDataRefetch } = useGetSupplierByIdQuery(formik.values.selectedSupplier?.value, {
    skip: !formik.values.selectedSupplier?.value
  });
  // Update supplier details when selected supplier changes
  useEffect(() => {
    if (singleSupplier?.data) {
      formik.setFieldValue('supplierDetails', {
        supplierName: singleSupplier.data.supplierName,
        phone: singleSupplier.data.phone,
        currentBalance: singleSupplier.data.currentBalance,
        address: singleSupplier.data.address
      });
    }
  }, [singleSupplier?.data]);

  const handleDateChange = (date) => {
    formik.setFieldValue('date', date);
  };

  const handleSupplierChange = (selectedOption) => {
    formik.setFieldValue('selectedSupplier', selectedOption);
  };

  const supplierTransactionColumns = [
    {
      title: "Transaction ID",
      dataIndex: "_id",
      render: (id) => <span>{id || "-"}</span>,
    },
    {
      title: "Supplier Name",
      dataIndex: ["supplierId", "supplierName"],
      render: (supplierName, record) => (
        <span>
          {supplierName || record.supplierId?.supplierName || "-"}
        </span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      sorter: (a, b) => a.amount - b.amount,
      render: (amount, record) => (
        <span style={{ color: record.type === 'debit' ? 'red' : 'green' }}>
          {amount.toFixed(2)}
        </span>
      ),
    },
    {
      title: "Type",
      dataIndex: "type",
      render: (type) => (
        <span style={{ color: type === 'debit' ? 'red' : 'green' }}>
          {type}
        </span>
      ),
      filters: [
        { text: 'Debit', value: 'debit' },
        { text: 'Credit', value: 'credit' },
      ],
      onFilter: (value, record) => record.type === value,
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      render: (date) => (
        <span>{new Date(date).toLocaleDateString()}, {new Date(date).toLocaleTimeString()}</span>
      ),
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
    },
    {
      title: "Remarks",
      dataIndex: "remarks",
      render: (remarks) => remarks || "-",
    }
  ];

  return (
    <div className="modal fade" id="add-supplier-transaction">
      <div className="modal-dialog add-centered">
        <div className="modal-content">
          <div className="page-wrapper p-0 m-0">
            <div className="content p-0">
              <div className="modal-header border-0 custom-modal-header">
                <div className="page-title">
                  <h4>Supplier Ledger</h4>
                </div>
                <button
                  type="button"
                  className="close"
                  data-bs-dismiss="modal"
                  aria-label="Close"
                >
                  <span aria-hidden="true">×</span>
                </button>
              </div>
              <div className="card">
                <div className="card-body">
                  <form onSubmit={formik.handleSubmit}>
                    <div className="row">
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Supplier Name</label>
                          <div className="row">
                            <div className="col-lg-10 col-sm-10 col-10">
                              <Select
                                classNamePrefix="react-select"
                                options={suppliersList}
                                value={formik.values.selectedSupplier}
                                onChange={handleSupplierChange}
                                placeholder="Select Supplier"
                              />
                              {formik.touched.selectedSupplier && formik.errors.selectedSupplier ? (
                                <div className="text-danger">{formik.errors.selectedSupplier}</div>
                              ) : null}
                            </div>
                            <div className="col-lg-2 col-sm-2 col-2 ps-0">
                              <div className="add-icon">
                                <Link to="#" className="choose-add"> 
                                  <PlusCircle className="plus" /> 
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Date</label>
                          <div className="input-groupicon calender-input">
                            <Calendar className="info-img" />
                            <DatePicker
                              value={formik.values.date}
                              onChange={handleDateChange}
                              className="filterdatepicker"
                              format="DD-MM-YYYY"
                              placeholder="Choose Date"
                            />
                          </div>
                          {formik.touched.date && formik.errors.date ? (
                            <div className="text-danger">{formik.errors.date}</div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Amount</label>
                          <input
                            type="number"
                            className="form-control"
                            placeholder="Enter Amount here"
                            name="amount"
                            value={formik.values.amount}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            required
                          />
                          {formik.touched.amount && formik.errors.amount ? (
                            <div className="text-danger">{formik.errors.amount}</div>
                          ) : null}
                        </div>
                      </div>
                      <div className="col-lg-4 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Remarks</label>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Remarks if any"
                            name="remarks"
                            value={formik.values.remarks}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                          />
                        </div>
                      </div>
                      <div className="col-sm-3 mt-4">
                        <div className="btn-group-horizontal" role="group" aria-label="Transaction type">
                          <input
                            type="radio"
                            className="btn-check"
                            name="transactionType"
                            id="credit"
                            value="credit"
                            checked={formik.values.transactionType === 'credit'}
                            onChange={() => formik.setFieldValue('transactionType', 'credit')}
                          />
                          <label className="btn btn-outline-success me-2" htmlFor="credit">Credit</label>
              
                          <input
                            type="radio"
                            className="btn-check"
                            name="transactionType"
                            id="debit"
                            value="debit"
                            checked={formik.values.transactionType === 'debit'}
                            onChange={() => formik.setFieldValue('transactionType', 'debit')}
                          />
                          <label className="btn btn-outline-danger me-2" htmlFor="debit">Debit</label>
              
                          <span className="mt-2">Selected: <strong>{formik.values.transactionType}</strong></span>
                        </div>
                        {formik.touched.transactionType && formik.errors.transactionType ? (
                          <div className="text-danger">{formik.errors.transactionType}</div>
                        ) : null}
                      </div>
                    </div>
                 
                    <div className="row">
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Supplier Name</label>
                          <input
                            type="text"
                            placeholder="Supplier Name"
                            readOnly={true}
                            className="form-control"
                            value={formik.values.supplierDetails.supplierName}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Mobile</label>
                          <input
                            type="text"
                            placeholder="Mobile"
                            readOnly={true}
                            className="form-control"
                            value={formik.values.supplierDetails.phone}
                          />
                        </div>
                      </div>
                    
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="input-blocks">
                          <label>Current Balance</label>
                          <input
                            type="text"
                            readOnly={true}
                            placeholder="credit"
                            className="form-control"
                            value={formik.values.supplierDetails.currentBalance}
                          />
                        </div>
                      </div>
                      <div className="col-lg-3 col-sm-6 col-12">
                        <div className="input-blocks mb-5">
                          <label>Address</label>
                          <input
                            type="text"
                            placeholder="Address"
                            readOnly={true}
                            className="form-control"
                            value={formik.values.supplierDetails.address}
                          />
                        </div>
                      </div>
                      <div className="col-lg-12 text-end">
                        <button
                          type="button"
                          className="btn btn-cancel add-cancel me-3"
                          data-bs-dismiss="modal"
                        >
                          Cancel
                        </button>
                        <button 
                          type="submit" 
                          className="btn btn-submit add-sale"
                          disabled={isLoading || !formik.isValid}
                        >
                          {isLoading ? 'Submitting...' : 'Submit'}
                        </button>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
              <div className="table-responsive">
                <Table 
                  columns={supplierTransactionColumns} 
                  dataSource={supplierTransactionData} 
                  rowKey="_id"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddSupplierTransaction;